import type { Hub } from "@foreman/app-core";
import type { Nav } from "./nav";
import { alertOpened } from "./analytics";

/** What a tapped alert says it is about. Everything but the event id is a hint: the card or session may be gone by now. */
export interface AlertTarget {
  eventId: string;
  sessionId?: string;
  kind?: string;
}

export const OPEN_ALERT_EVENT = "foreman:open-alert";

/** A cold start from a tapped alert carries its target in the address (`?open=inbox&event=…&session=…&kind=…`). */
export function alertFromUrl(search: string): AlertTarget | null {
  const q = new URLSearchParams(search);
  const eventId = q.get("event");
  if (q.get("open") !== "inbox" || !eventId) return null;
  return { eventId, ...(q.get("session") && { sessionId: q.get("session")! }), ...(q.get("kind") && { kind: q.get("kind")! }) };
}

/** The card, scrolled into view and lit for a moment. Waits briefly for the screen to draw it. */
function focusCard(eventId: string, tries = 20): void {
  const card = document.querySelector(`[data-event="${CSS.escape(eventId)}"]`);
  if (!card) return void (tries > 0 && setTimeout(() => focusCard(eventId, tries - 1), 100));
  card.scrollIntoView({ block: "center", behavior: "smooth" });
  card.classList.add("alert-flash");
  setTimeout(() => card.classList.remove("alert-flash"), 2200);
}

/**
 * Land where an alert points. An approval or a question goes to its card in the Inbox. A finished or failed one goes to the
 * session itself when that is still running (Back returns to the Inbox). The event may not have arrived yet when the app was
 * just opened, so this waits a few seconds; if it never does, the person is left on the Inbox, which is the old behaviour.
 */
export function openAlert(hub: Hub, nav: Nav, target: AlertTarget, waitMs = 8000): () => void {
  alertOpened(target.kind);
  if (nav.view.name !== "inbox") nav.goTab({ name: "inbox" });
  let done = false;
  let off: (() => void) | null = null;
  const finish = () => {
    done = true;
    off?.();
    clearTimeout(timer);
  };
  const check = () => {
    if (done) return;
    const item = hub.getInbox().find((i) => i.event.id === target.eventId);
    if (!item) return;
    const { event, computer } = item;
    if ((event.kind === "completed" || event.kind === "failed") && !event.external) {
      const snap = hub.get(computer.id)?.getSnapshot();
      if (!snap || snap.state !== "ready") return; // the session list comes with the connection: wait for it
      finish();
      if (snap.sessions.some((s) => s.id === event.sessionId && s.status === "running")) return nav.push({ name: "terminal", computerId: computer.id, sessionId: event.sessionId });
      return focusCard(event.id);
    }
    finish();
    focusCard(event.id);
  };
  const timer = setTimeout(finish, waitMs);
  off = hub.subscribe(check);
  check();
  return finish;
}
